import { Flex } from "antd";
import { Shield, Sparkles, Target, Users } from "lucide-react";
import { AiFitAnalysis, getRiskTone } from "../lib/aiFitCheckUtils";
import { AudienceCard, BulletList, DetailRow, MetricCard } from "./parts";
import styles from "./styles.module.scss";

export function DetailsTab({ data }: { data: AiFitAnalysis }) {
  const audience = data.audience_match;
  const profile = data.creator_profile_match;
  const usage = data.campaign_usage_recommendation;
  const risk = data.risk_assessment;
  const metrics = data.creator_metrics_snapshot;
  const riskTone = getRiskTone(risk?.overall_risk_level);

  return (
    <Flex vertical gap={20}>
      {audience ? (
        <div className={styles.panel}>
          <h3 className={styles.panelTitle}>
            <Users size={14} className={styles.iconAccent} />
            Audience match
          </h3>
          {audience.audience_fit_summary ? (
            <p className={styles.bodyText}>{audience.audience_fit_summary}</p>
          ) : null}
          <div className={styles.twoCol}>
            <AudienceCard label="Age" match={audience.age_match} />
            <AudienceCard label="Gender" match={audience.gender_match} />
            <AudienceCard label="Location" match={audience.location_match} />
            <AudienceCard label="Interest" match={audience.interest_match} />
          </div>
        </div>
      ) : null}

      {profile ? (
        <div className={styles.panel}>
          <h3 className={styles.panelTitle}>
            <Sparkles size={14} className={styles.iconAccent} />
            Creator profile
          </h3>
          <DetailRow label="Category" value={profile.creator_category_match} />
          <DetailRow label="Language" value={profile.creator_language_match} />
          <DetailRow label="Location" value={profile.creator_location_match} />
          <DetailRow label="Gender" value={profile.creator_gender_match} />
          <DetailRow label="Persona" value={profile.creator_persona_match} />
          <DetailRow label="Archetype" value={profile.creator_archetype_fit} />
        </div>
      ) : null}

      {usage ? (
        <div className={styles.panel}>
          <h3 className={styles.panelTitle}>
            <Target size={14} className={styles.iconAccent} />
            Campaign usage
          </h3>
          <DetailRow label="Best use case" value={usage.best_use_case} />
          <DetailRow
            label="Content angle"
            value={usage.recommended_content_angle}
          />
          <DetailRow
            label="Integration style"
            value={usage.best_integration_style}
          />
          <DetailRow label="Why" value={usage.why_this_use_case} />
        </div>
      ) : null}

      {risk ? (
        <div className={styles.panel}>
          <h3 className={styles.panelTitle}>
            <Shield size={14} className={styles.iconAccent} />
            Risk assessment
          </h3>
          {risk.overall_risk_level ? (
            <span
              className={`${styles.riskPill} ${styles[`risk_${riskTone}`]}`}
            >
              {risk.overall_risk_level} risk
            </span>
          ) : null}
          {risk.risk_summary ? (
            <p className={styles.bodyText}>{risk.risk_summary}</p>
          ) : null}
          <div className={styles.twoCol}>
            <div>
              <p className={styles.miniLabel}>Brand safety</p>
              <BulletList items={risk.brand_safety_risks} />
            </div>
            <div>
              <p className={styles.miniLabel}>Audience mismatch</p>
              <BulletList items={risk.audience_mismatch_risks} />
            </div>
          </div>
        </div>
      ) : null}

      {metrics ? (
        <div className={styles.panel}>
          <h3 className={styles.panelTitle}>Creator metrics</h3>
          <div className={styles.metricsGrid}>
            <MetricCard
              label="Followers"
              value={metrics.followers?.toLocaleString() ?? "—"}
            />
            <MetricCard
              label="Avg reel views"
              value={metrics.avg_reel_views?.toLocaleString() ?? "—"}
            />
            <MetricCard
              label="Engagement"
              value={`${metrics.engagement_rate ?? "—"}%`}
            />
            <MetricCard
              label="Real followers"
              value={`${metrics.real_follower_percentage ?? "—"}%`}
            />
          </div>
          <DetailRow
            label="Top cities"
            value={metrics.top_audience_cities?.join(", ")}
          />
          <DetailRow
            label="Dominant age"
            value={metrics.dominant_audience_age_bucket}
          />
          <DetailRow
            label="Dominant gender"
            value={metrics.dominant_audience_gender}
          />
          <DetailRow label="Verified" value={metrics.verified ? "Yes" : "No"} />
        </div>
      ) : null}
    </Flex>
  );
}
